import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { ScheduleI } from '~src/interfaces/schedule.interface';
import { Segment } from '~src/interfaces/segment.interface';
import { fetchSearchSegments } from '~src/services/fetch/fetchSearchSegments.service';
import HoverInfoIcon from '~src/shared/tooltips/HoverInfoIcon';
import { Survey, SurveyScopes } from '~src/modules/surveys/interfaces';

import {
  ChipsError,
  HeaderContainer,
  InputsSection,
  PeopleSectionHeader,
  SchedulesItemSection,
  SectionLine,
  TopicDescription,
} from '~src/modules/surveys/components/schedules/scheduleItem/templates/scheduleTemplate.sc';
import CheckBoxSection from './CheckBoxSection';

import PeopleChips from './customInputs/PeopleChips';
import { SurveySubjectTypes, SurveyUseCase } from '~src/interfaces/surveys.interfaces';

interface Props {
  schedule: ScheduleI;
  oldSchedule: ScheduleI;
  onChangeBox: (active: boolean) => void;
  survey: Survey;
  deleteError: (error: string) => void;
  setInputRef: (node: HTMLInputElement) => void;
  getAboutPeople: (people: Segment[], key: string) => void;
  error: boolean;
  useCaseKey: string;
  changeFlag: (value: boolean, key: keyof ScheduleI) => void;
}

const AboutSection: FC<Props> = (props) => {
  const [t] = useTranslation();
  const {
    error,
    survey: { useCase, subject },
  } = props;

  const headerString =
    subject ===  SurveySubjectTypes.Groups ? '.groups.header' : useCase === SurveyUseCase.BottomUp ? '.bottomUp' : '.header';

  const isAdminScope = props.survey.scope === SurveyScopes.Admin;
  const isUserScope = props.survey.scope === SurveyScopes.User;

  const locked = props.survey.scope !== SurveyScopes.Admin && props.survey.scope !== SurveyScopes.User;

  const isSelf360Survey = props.survey.useCase === SurveyUseCase.ThreeSixty && isUserScope

  return (
    <SchedulesItemSection>
      <SectionLine>
        <PeopleSectionHeader>
          <HeaderContainer data-translation={`x.surveys.schedule.aboutsection${headerString}`}>
            {t(`x.surveys.schedule.aboutsection${headerString}${isUserScope ? '.user_request' : ''}`)}
            {isAdminScope && (
              <TopicDescription>
                <HoverInfoIcon
                  infoText={{
                    key: `x.surveys.schedule.aboutsection.header.info_box.aboutsection.${subject}${props.useCaseKey}`,
                    txt: t(
                      `x.surveys.schedule.aboutsection.header.info_box.aboutsection.${subject}${props.useCaseKey}`,
                    ),
                  }}
                />
              </TopicDescription>
            )}
          </HeaderContainer>
          {error && (
            <ChipsError data-translation="x.surveys.schedule.asksection.errormessage">
              {t('x.surveys.schedule.asksection.errormessage')}
            </ChipsError>
          )}
        </PeopleSectionHeader>
      </SectionLine>
      <InputsSection $locked={isSelf360Survey}>
        <SectionLine $noPadding>
          <PeopleChips
            locked={isSelf360Survey}
            placeHolderKey={`x.surveys.schedule.input.about.placeholder.${useCase || subject}`}
            setInputRef={props.setInputRef}
            fetchSuggestions={fetchSearchSegments}
            chipsName="about"
            selectedPeople={props.schedule.about || []}
            onChangePeople={props.getAboutPeople}
            filter={subject === SurveySubjectTypes.Groups ? 'group' : undefined}
            error={error}
            deleteError={props.deleteError}
          />
        </SectionLine>
        <SectionLine>
          {subject !== SurveySubjectTypes.Groups && (
            <CheckBoxSection
              locked={locked}
              useCase={useCase}
              subject={subject}
              oldSchedule={props.oldSchedule}
              schedule={props.schedule}
              scope={props.survey.scope}
              withToggleBox={useCase === SurveyUseCase.BottomUp || useCase === SurveyUseCase.TopDown}
              title={t(`x.surveys.schedule.asksection.checkbox.${useCase}`)}
              titleKey={`x.surveys.schedule.asksection.checkbox.${useCase}`}
              toggleBoxTitle={t(
                `x.surveys.schedule.aboutsection.togglebox.title.${subject}${props.useCaseKey}`,
              )}
              onChangeToggleBox={props.onChangeBox}
              active={props.schedule.anonymous}
              changeFlag={props.changeFlag}
            />
          )}
        </SectionLine>
      </InputsSection>
    </SchedulesItemSection>
  );
};

export default AboutSection;
